/**
 * Prezzi e date dei listini nella lingua di chi guarda: le funzioni del gestionale (`core/i18n/formattazione.ts`), mai
 * `toLocaleString` scritto a mano. I prezzi del listino sono in euro (non in centesimi); `data_documento` è un giorno di
 * calendario (`AAAA-MM-GG`) e si formatta in UTC, così nessun fuso lo fa scivolare al giorno prima.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { creaContestoLocale } from '@gestionale/core/i18n/lingue';
import { formatData, formatPercentuale, formatValuta } from '@gestionale/core/i18n/formattazione';

export interface FormatiListino {
  euro: (valore: number | null | undefined) => string;
  giorno: (data: string | null | undefined) => string;
  momento: (data: Date | null) => string;
  /** «+4,5%» / «−3,2%»: quanto cambia un prezzo. */
  variazione: (prima: number | null, dopo: number | undefined) => string;
}

export function useFormatiListino(): FormatiListino {
  const { i18n } = useTranslation('listini');
  return useMemo(() => {
    const ctx = creaContestoLocale({ lingua: i18n.language, paese: 'IT', valuta: 'EUR', fusoOrario: 'Europe/Rome' });
    const calendario = creaContestoLocale({ lingua: i18n.language, paese: 'IT', valuta: 'EUR', fusoOrario: 'UTC' });
    return {
      euro: (valore) => (typeof valore === 'number' ? formatValuta(valore, ctx) : ''),
      giorno: (data) => (data ? formatData(`${data}T00:00:00Z`, calendario, 'media') : ''),
      momento: (data) => (data ? formatData(data, ctx, 'dataOra') : ''),
      variazione: (prima, dopo) => {
        if (typeof prima !== 'number' || typeof dopo !== 'number' || prima === 0) return '';
        const v = ((dopo - prima) / prima) * 100;
        return `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatPercentuale(Math.abs(v), ctx, { decimaliMax: 1 })}`;
      },
    };
  }, [i18n.language]);
}
